import axios from 'axios';
import { defineStore } from 'pinia';
import { sortObjectArray } from '@utils/sort-by-object-property';

const REFRESH_INTERVAL = 5 * 60 * 1000;

const useNotificationsStore = defineStore('notificationsStore', {
  state: () => ({
    notifications: [],
    notificationRefreshInterval: undefined,
    retryCount: 0,
  }),

  getters: {
    unreadNotifications(state) {
      return state.notifications.filter(n => !n.read_at);
    },

    unreadCount() {
      return this.unreadNotifications.length;
    },
  },

  actions: {
    getNotificationId(notification) {
      return notification?.notification_id || notification?.id;
    },

    getNotifications() {
      this.$logger.info('Getting notifications for user');

      return this.$http
        .get('/api/notification/notifications')
        .then(res => {
          this.$logger.info('Got notifications');

          this.notifications = sortObjectArray(
            res.data.notifications,
            'posted_at',
            true,
          );

          this.retryCount = 0;
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            this.retryCount += 1;

            if (this.retryCount <= 3) {
              this.$logger.warn(
                'Request to get notifications cancelled: retrying',
                undefined,
                err,
              );

              return this.getNotifications();
            }

            this.$logger.warn(
              'Cancellation retries exceeded for this attempt',
              undefined,
              err,
            );

            this.retryCount = 0;
            return undefined;
          }

          if (this.$http.errIn(err, [401, 403])) {
            this.$logger.warn(
              'User was not authorised to get notifications',
              undefined,
              err,
            );
          } else {
            this.$logger.warn('Error getting notification', undefined, err);
          }

          this.retryCount = 0;
          return undefined;
        });
    },

    startNotificationRefreshCycle() {
      this.$logger.info(
        'Starting recurring notification check',
        undefined,
        true,
      );

      clearInterval(this.notificationRefreshInterval);

      this.notificationRefreshInterval = setInterval(() => {
        this.getNotifications();
      }, REFRESH_INTERVAL);
    },

    stopNotificationRefreshCycle() {
      if (this.notificationRefreshInterval !== undefined) {
        clearInterval(this.notificationRefreshInterval);
        this.notificationRefreshInterval = undefined;
      }
    },

    readNotification(notificationId) {
      this.$logger.info('Marking notification as read', { notificationId }, true);

      return this.$http
        .put(`/api/notification/${notificationId}/read`)
        .then(() => {
          this.$logger.info('Notification marked as read', { notificationId });
        })
        .catch(err => {
          this.$logger.error(
            'Error marking notification as read',
            { notificationId },
            err,
          );
        });
    },

    markNotificationAsRead(notification) {
      if (!notification || notification.read_at) return;

      const notificationId = this.getNotificationId(notification);
      if (!notificationId) return;

      const index = this.notifications.findIndex(
        n => this.getNotificationId(n) === notificationId,
      );

      if (index !== -1) {
        const updated = {
          ...this.notifications[index],
          read_at: new Date(),
        };

        this.notifications.splice(index, 1, updated);
      }

      this.readNotification(notificationId);
    },

    readAllNotifications() {
      this.notifications
        .filter(n => !n.read_at)
        .forEach(n => this.markNotificationAsRead(n));
    },

    clear() {
      this.stopNotificationRefreshCycle();
      this.notifications = [];
      this.retryCount = 0;
    },
  },
});

export default useNotificationsStore;
